import { Button, LinkButton, QuietButton } from "../ui/button";
import { Screen } from "../ui/screen";
import { HouseRulesViewer } from "./viewer";
import { relativeTime } from "../../lib/format";
import { humaniseAuthor } from "../../lib/rules";
import type { HouseRules, RulesVersionSummary } from "../../lib/api";

type ListProps = {
  versions: RulesVersionSummary[];
  currentVersion: number;
  loading: boolean;
  error: string | null;
  onOpen: (version: number) => void;
  onBack: () => void;
};

/**
 * FR-8.5: the version history is a trust surface. Who wrote it, when, and what
 * they said they were doing — in that order, because that is what is asked.
 */
export function RulesHistory({
  versions,
  currentVersion,
  loading,
  error,
  onOpen,
  onBack,
}: ListProps) {
  return (
    <Screen wide>
      <p className="mb-6">
        <LinkButton type="button" onClick={onBack}>
          Your rules
        </LinkButton>
      </p>

      <p className="text-[13px] uppercase tracking-[0.18em] text-faint">History</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-text sm:text-4xl">
        Every version is kept.
      </h1>
      <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-muted">
        Read any of them. Restore any of them. Nothing you have written is lost.
      </p>

      <div className="mt-8" aria-live="polite">
        {loading && <p className="nob-breathe text-sm text-faint">Reading the history.</p>}
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        {!loading && !error && versions.length === 0 && (
          <p className="text-sm text-faint">There is only the version you are looking at.</p>
        )}
      </div>

      {versions.length > 0 && (
        <ul className="mt-2 divide-y divide-border-soft border-y border-border-soft">
          {versions.map((v) => (
            <li key={v.version}>
              <button
                type="button"
                onClick={() => onOpen(v.version)}
                className="group flex w-full items-baseline gap-4 px-1 py-4 text-left transition-colors
                           duration-75 hover:bg-surface/40 focus-visible:outline-none focus-visible:ring-2
                           focus-visible:ring-focus/70"
              >
                <span
                  className={`font-mono text-[13px] tabular-nums ${
                    v.version === currentVersion ? "text-accent-ink" : "text-faint"
                  }`}
                >
                  v{v.version}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] leading-relaxed text-text-soft">
                    {v.change_note ?? "No note."}
                  </span>
                  <span className="mt-1 block text-xs text-fainter">
                    {humaniseAuthor(v.author)}
                    {relativeTime(v.created_at) ? ` · ${relativeTime(v.created_at)}` : ""}
                    {v.version === currentVersion ? " · current" : ""}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}

type ViewProps = {
  rules: HouseRules;
  currentVersion: number;
  busy: boolean;
  error: string | null;
  onRestore: () => void;
  onBack: () => void;
};

/** One old version, read in full before anyone decides to bring it back. */
export function RulesVersionView({
  rules,
  currentVersion,
  busy,
  error,
  onRestore,
  onBack,
}: ViewProps) {
  const current = rules.version === currentVersion;

  return (
    <Screen wide>
      <p className="mb-6">
        <LinkButton type="button" onClick={onBack}>
          History
        </LinkButton>
      </p>

      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[13px] uppercase tracking-[0.18em] text-faint">
          Version {rules.version}
        </p>
        {current && (
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-accent-ink/80">
            Current
          </span>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-surface/30 p-7 sm:p-9">
        <HouseRulesViewer markdown={rules.markdown} />
      </div>

      <div className="mt-6 min-h-[1.5rem]" aria-live="polite">
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!current && (
          <Button type="button" onClick={onRestore} disabled={busy}>
            Restore this version
          </Button>
        )}
        <QuietButton type="button" onClick={onBack} disabled={busy}>
          Back
        </QuietButton>
      </div>

      {!current && (
        <p className="mt-4 text-xs text-fainter">
          Restoring creates a new version. Nothing is lost.
        </p>
      )}
    </Screen>
  );
}
